import React, { useCallback } from 'react';
import type { RecommendationsServingConfig } from '@noths/polaris-client-listings-api';
import { ProductCarousel } from '@noths/polaris-client-ribbons-design-system';

import type { RecommendedProductsViewModel } from 'src/components/layouts/hooks/useRecommendedProductsViewModel';
import { useListingsRecommendations } from '../hook/useListingsRecommendations';
import {
  trackRecommendedProductClick,
  trackRecommendedProductsCarouselNavigation,
  trackRecommendedProductsImpression,
} from '../tracking/recommendedProductCarouselEvents';

/**
 * The products come from a client-side listings request rather than the store,
 * so the request inputs and the selected option values its events carry are
 * passed in by useRecommendedProductsViewModel. Nothing here reads the store.
 */
export interface RecommendedProductsCarouselContainerProps extends RecommendedProductsViewModel {
  readonly enableFreeDomesticDeliveryLabels?: boolean;
  readonly hideLabelIcons?: boolean;
  readonly servingConfig: RecommendationsServingConfig;
  title: string;
}

export const RecommendedProductsCarouselContainer = ({
  currencyCode,
  enableFreeDomesticDeliveryLabels,
  hideLabelIcons,
  partnerShortCode,
  productCode,
  selectedOptionValues,
  servingConfig,
  title,
  visitorId,
}: RecommendedProductsCarouselContainerProps) => {
  const { recommendedProducts } = useListingsRecommendations({
    currencyCode,
    partnerShortCode,
    productCode,
    servingConfig,
    visitorId,
  });

  const onVisible = useCallback(() => {
    if (recommendedProducts?.length === 0) {
      return;
    }

    trackRecommendedProductsImpression({
      products: recommendedProducts,
      selectedOptionValues,
      servingConfig,
    });
  }, [selectedOptionValues, recommendedProducts, servingConfig]);

  const onProductClick = useCallback(
    (evt: React.MouseEvent, { index }: { index: number }) => {
      if (!recommendedProducts || recommendedProducts?.length === 0) {
        return;
      }

      trackRecommendedProductClick(evt, {
        product: recommendedProducts[index],
        productIndex: index,
        selectedOptionValues,
        servingConfig,
      });
    },
    [selectedOptionValues, recommendedProducts, servingConfig],
  );

  const onNavigation = useCallback(
    (swiped: boolean, control: 'next' | 'prev') => {
      trackRecommendedProductsCarouselNavigation({
        servingConfig,
        navigation: {
          type: swiped ? 'Scroll' : 'Arrow Click',
          direction: control === 'next' ? 'Right' : 'Left',
        },
      });
    },
    [servingConfig],
  );

  if (!recommendedProducts || recommendedProducts.length === 0) {
    return null;
  }

  return (
    <ProductCarousel
      currency={currencyCode}
      enableFreeDomesticDeliveryLabels={enableFreeDomesticDeliveryLabels}
      hideLabelIcons={hideLabelIcons}
      lazyImages
      onNavigation={onNavigation}
      onProductClick={onProductClick}
      onVisible={onVisible}
      products={recommendedProducts}
      title={title}
    />
  );
};
